import type { ActionFlowStateDiff, ActionFlowTraceEntry } from '../types'

export interface ActionFlowStateTopologyRow {
  id: 'local' | 'appData' | 'ui' | 'runtime' | 'external' | 'results' | 'computed' | 'renderer'
  label: string
  value: string
}

export interface ActionFlowStateTopologySummary {
  changedPathCount: number
  rows: ActionFlowStateTopologyRow[]
}

function plural(count: number, singular: string, pluralValue = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralValue}`
}

function summarizePaths(diffs: ActionFlowStateDiff[], emptyLabel: string): string {
  if (diffs.length === 0) return emptyLabel
  const paths = Array.from(new Set(diffs.map((diff) => diff.path))).slice(0, 2)
  return `${plural(diffs.length, 'change')} · ${paths.join(', ')}`
}

function summarizeKeys(record: Record<string, unknown>, emptyLabel: string): string {
  const keys = Object.keys(record)
  if (keys.length === 0) return emptyLabel
  return `${plural(keys.length, 'key')} · ${keys.slice(0, 2).join(', ')}`
}

export function summarizeActionFlowStateTopology(trace: ActionFlowTraceEntry): ActionFlowStateTopologySummary {
  const localDiffs = trace.stateDiffs.filter((diff) => (
    diff.path.startsWith('local.') ||
    diff.domain === 'block_logic'
  ))
  const appDataDiffs = trace.stateDiffs.filter((diff) => (
    diff.path.startsWith('appData.') ||
    diff.domain === 'application_global'
  ))
  const uiDiffs = trace.stateDiffs.filter((diff) => diff.domain === 'ui')
  const runtimeDiffs = trace.stateDiffs.filter((diff) => diff.domain === 'runtime')
  const externalEvidenceCount = trace.actionCalls.filter((call) => call.dataSource === 'external').length
  const changedPathCount = new Set(trace.stateDiffs.map((diff) => diff.path)).size

  const rows: ActionFlowStateTopologyRow[] = [
    { id: 'local', label: 'Local state', value: summarizePaths(localDiffs, 'no local writes') },
    { id: 'appData', label: 'App data', value: summarizePaths(appDataDiffs, 'no app-wide writes') },
    { id: 'results', label: '_results', value: summarizeKeys(trace.results, 'no named results') },
    { id: 'computed', label: 'Computed', value: summarizeKeys(trace.computed, 'no computed projection') },
    {
      id: 'renderer',
      label: 'Renderer',
      value: trace.rendererProjection.length > 0
        ? plural(trace.rendererProjection.length, 'projection')
        : 'no renderer projection',
    },
  ]

  if (uiDiffs.length > 0) {
    rows.splice(2, 0, { id: 'ui', label: 'UI state', value: summarizePaths(uiDiffs, 'no UI writes') })
  }
  if (runtimeDiffs.length > 0) {
    rows.splice(2, 0, { id: 'runtime', label: 'Runtime', value: summarizePaths(runtimeDiffs, 'no runtime writes') })
  }
  if (externalEvidenceCount > 0) {
    rows.splice(2, 0, {
      id: 'external',
      label: 'External',
      value: plural(externalEvidenceCount, 'call'),
    })
  }

  return { changedPathCount, rows }
}
